// @mentions: finding the one being typed, and turning a pick into text. Lifted
// as is from the Andon app's `lib/mentions.ts`.
//
// Pulled out of the composer because none of it is rendering, and because the
// part worth testing is where the token starts and ends. Getting that wrong is
// how a picker either never opens, or eats half the sentence in front of it.
//
// THE PICKED LOGINS ARE THE TRUTH, NOT THE TEXT.
//
// The body that goes to Odoo carries `@Ben Curtis`, because that is what reads
// on a thread. The SERVER is sent the login list from the picker. Names are not
// unique, people get renamed, and a name matched server-side out of free text
// would silently notify nobody on a typo — or notify the wrong Ben. So the text
// is for humans and the list is for the machine, and `stillMentioned` is what
// keeps the two honest when somebody edits the draft afterwards.

export interface Person {
  login: string;
  name: string;
}

export interface MentionQuery {
  /** Where the `@` is. */
  start: number;
  /** Where the caret is — the end of what has been typed so far. */
  end: number;
  /** What follows the `@`, lowercased. Empty right after typing `@`. */
  term: string;
}

/**
 * The mention being typed at the caret, or null.
 *
 * An `@` only opens the picker when it STARTS A WORD — after whitespace, or at
 * the very beginning. That is what stops an email address in the middle of a
 * sentence turning into a person-picker halfway through typing it, which on a
 * thread where people paste addresses is not a rare case.
 *
 * The term runs to the caret and may contain a single space, because "Ben C"
 * has to reach Ben Curtis and people type surnames. It gives up at two
 * consecutive spaces or a third word: that is prose that happens to follow an
 * `@`, and the picker should have closed by then.
 */
export function mentionAt(text: string, caret: number): MentionQuery | null {
  const upto = text.slice(0, caret);
  const at = upto.lastIndexOf('@');
  if (at < 0) return null;
  const before = at === 0 ? '' : upto[at - 1]!;
  if (before && !/\s/.test(before)) return null;
  const term = upto.slice(at + 1);
  if (/[\r\n]/.test(term)) return null;
  if (term.includes('  ')) return null;
  if (term.split(' ').length > 2) return null;
  return { start: at, end: caret, term: term.toLowerCase() };
}

/**
 * The people worth offering for what has been typed. Ranked, then capped.
 *
 * Generic over the row rather than narrowing to `Person`, because the callers
 * pass richer ones — the @ picker's roster carries a department and a shift
 * badge, the handover sheet's carries a uid — and a `Person[]` return would
 * throw all of that away. The uid is the one that matters: it is how the sheet
 * asks for somebody's photograph.
 */
export function matches<T extends Person>(people: T[], term: string, limit = 5): T[] {
  if (!term) return people.slice(0, limit);
  const t = term.toLowerCase();
  const scored = people
    .map((p): { p: T; rank: number } | null => {
      const name = p.name.toLowerCase();
      // WORD PREFIXES, never substring-anywhere. People type surnames as often
      // as first names, so every word is a candidate — but matching inside a
      // word puts Dean Whit(co)mbe in the list for "be", which is noise at the
      // exact moment somebody is trying to pick one name quickly.
      if (name.startsWith(t)) return { p, rank: 0 };
      if (name.split(/\s+/).some((word) => word.startsWith(t))) return { p, rank: 1 };
      return null;
    })
    .filter((x): x is { p: T; rank: number } => x != null)
    .sort((a, b) => a.rank - b.rank || a.p.name.localeCompare(b.p.name));
  return scored.slice(0, limit).map((x) => x.p);
}

/** Replace the token being typed with the person's name, and a trailing space. */
export function applyMention(text: string, q: MentionQuery, person: Person): { text: string; caret: number } {
  const head = text.slice(0, q.start);
  const tail = text.slice(q.end);
  // The trailing space is what takes the caret out of the mention token, so it
  // has to be there — but only if the text does not already provide one.
  // Mentioning somebody in the middle of a sentence already written puts the
  // insertion straight before an existing space, and adding another gives
  // "@Dean Whitcombe  to bring them".
  const spaced = /^\s/.test(tail);
  const inserted = `@${person.name}${spaced ? '' : ' '}`;
  return {
    text: head + inserted + tail,
    // Past the space either way, so the next word is typed rather than
    // appended to the name — which would re-open the picker on a mention that
    // has already been made.
    caret: q.start + inserted.length + (spaced ? 1 : 0),
  };
}

/**
 * Which of the picked people are still named in the draft.
 *
 * The list and the text drift the moment somebody backspaces over a name they
 * inserted, and a mention that is no longer written is a person subscribed to
 * an andon with no visible reason why. So the text has the final say on the
 * way out: picked, AND still written, or it does not go.
 */
export function stillMentioned(text: string, picked: Person[]): string[] {
  const lower = text.toLowerCase();
  return picked.filter((p) => lower.includes(`@${p.name.toLowerCase()}`)).map((p) => p.login);
}
